import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

interface Row { sessions: number; page_views: number; product_views: number; checkouts: number; purchases: number; revenue: number }
interface Report { totals: Row & { signups: number; conversion: number }; sources: (Row & { source: string })[]; products: (Row & { id: string; name: string; type: string })[];
  revenue_by_type: Record<string, number>; top_pages: [string, number][]; pinterest_campaigns: [string, number][] }

const money = (n: number) => `$${n.toFixed(2)}`;

function Totals({ t }: { t: Report["totals"] }) {
  const items: [string, string][] = [["Visits", String(t.sessions)], ["Product views", String(t.product_views)], ["Checkouts started", String(t.checkouts)],
    ["Purchases", String(t.purchases)], ["Revenue", money(t.revenue)], ["Visit to purchase", `${t.conversion}%`], ["Email signups", String(t.signups)]];
  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-7">
      {items.map(([l, v]) => (
        <div key={l} className="rounded-2xl bg-[#FDFBF7] p-3" data-testid={`analytics-${l.toLowerCase().replace(/ /g, "-")}`}>
          <p className="font-display text-2xl font-black text-[#23324A]">{v}</p><p className="text-xs font-semibold text-[#23324A]/60">{l}</p>
        </div>
      ))}
    </div>
  );
}

function Table({ title, head, rows, testId }: { title: string; head: string[]; rows: (string | number)[][]; testId: string }) {
  return (
    <div data-testid={testId}>
      <h3 className="mb-2 font-display text-base font-extrabold text-[#23324A]">{title}</h3>
      {rows.length === 0 ? <p className="text-sm text-[#23324A]/55">No data yet.</p> : (
        <div className="overflow-x-auto"><table className="w-full text-left text-sm">
          <thead><tr className="text-xs text-[#23324A]/55">{head.map((h) => <th key={h} className="py-1 pr-3 font-semibold">{h}</th>)}</tr></thead>
          <tbody>{rows.map((r, i) => <tr key={i} className="border-t border-[#23324A]/10">{r.map((c, j) => <td key={j} className="py-1.5 pr-3 text-[#23324A]">{c}</td>)}</tr>)}</tbody>
        </table></div>
      )}
    </div>
  );
}

function GaSetting() {
  const [id, setId] = useState("");
  useEffect(() => { api.get("/admin/analytics/settings").then((r) => setId(r.data.ga4_id || "")).catch(() => {}); }, []);
  const save = () => api.put("/admin/analytics/settings", { ga4_id: id.trim().toUpperCase() }).then(() => toast.success("Google Analytics ID saved")).catch((e) => toast.error(formatApiError(e)));
  return (
    <div className="flex flex-wrap items-center gap-2 text-sm" data-testid="ga4-setting">
      <span className="font-semibold text-[#23324A]">Google Analytics 4 ID</span>
      <input value={id} onChange={(e) => setId(e.target.value)} placeholder="G-XXXXXXXXXX" className="w-40 rounded-xl border-2 border-[#23324A]/20 px-3 py-1.5" data-testid="ga4-input" />
      <button className="btn-outline" onClick={save} data-testid="ga4-save">Save</button>
    </div>
  );
}

export function SalesAnalytics() {
  const [days, setDays] = useState(30);
  const [r, setR] = useState<Report | null>(null);
  useEffect(() => { api.get("/admin/analytics", { params: { days } }).then((x) => setR(x.data)).catch(() => setR(null)); }, [days]);
  return (
    <section className="calm-card mt-6 space-y-6 p-5 sm:p-6" data-testid="sales-analytics">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="font-display text-xl font-black text-[#23324A]">Traffic & sales</h2>
        <div className="flex gap-2">{[7, 30, 90].map((d) => <button key={d} className={`chip ${days === d ? "chip-active" : ""}`} onClick={() => setDays(d)} data-testid={`analytics-days-${d}`}>{d} days</button>)}</div>
      </div>
      {r && <Totals t={r.totals} />}
      {r && (
        <div className="grid gap-6 lg:grid-cols-2">
          <Table title="Where visitors come from" testId="analytics-sources" head={["Source", "Visits", "Product views", "Checkouts", "Sales", "Revenue"]}
            rows={r.sources.map((s) => [s.source, s.sessions, s.product_views, s.checkouts, s.purchases, money(s.revenue)])} />
          <Table title="Products" testId="analytics-products" head={["Product", "Type", "Views", "Checkouts", "Sales", "Revenue"]}
            rows={r.products.map((p) => [p.name, p.type, p.product_views, p.checkouts, p.purchases, money(p.revenue)])} />
          <Table title="Revenue by product type" testId="analytics-types" head={["Type", "Revenue"]} rows={Object.entries(r.revenue_by_type).map(([k, v]) => [k, money(v)])} />
          <Table title="Pinterest boards sending visits" testId="analytics-pinterest" head={["Board campaign", "Page views"]} rows={r.pinterest_campaigns} />
          <Table title="Top pages" testId="analytics-pages" head={["Page", "Views"]} rows={r.top_pages} />
        </div>
      )}
      <GaSetting />
    </section>
  );
}
